import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import toast from "react-hot-toast";

import PatientChip from "../../../components/chips/patient-chip";
import StatusChip from "../../../components/chips/status-chip";
import { useGetCurrentUser, useUpdatePatientStatus } from "../../../store";
import { ActiveStatus, PatientSummary, UserType } from "../../../types";
import { userHasPermission } from "../../../types-helpers";

interface EditablePatientStatusChipProps {
  patient: PatientSummary;
  size?: "small" | "large";
}

const EditablePatientStatusChip: React.FC<EditablePatientStatusChipProps> = ({
  patient,
  size = "small",
}) => {
  const { data: currentUser } = useGetCurrentUser();
  const canEditStatus = userHasPermission(UserType.SysAdmin, currentUser);

  const currentStatus =
    patient.status === ActiveStatus.Active
      ? ActiveStatus.Active
      : ActiveStatus.Inactive;
  const targetStatus =
    currentStatus === ActiveStatus.Active
      ? ActiveStatus.Inactive
      : ActiveStatus.Active;

  const [open, setOpen] = React.useState(false);
  const [isTargetSelected, setIsTargetSelected] = React.useState(false);

  const onSuccess = () => {
    toast.success(
      targetStatus === ActiveStatus.Active
        ? "Patient marked active"
        : "Patient marked inactive",
    );
    setOpen(false);
  };

  const onError = (error: unknown) => {
    console.error(error);
    toast.error("Failed to update patient status");
  };

  const { mutate: updatePatientStatus, isPending } = useUpdatePatientStatus(
    onSuccess,
    onError,
  );

  const statusChipProps =
    size === "large"
      ? {
          size: "medium" as const,
          height: 32,
          fontSize: "0.95rem",
          sx: {
            alignSelf: "center",
            cursor: canEditStatus ? "pointer" : "default",
            "& .MuiChip-label": {
              px: 1.5,
            },
          },
        }
      : {
          size: "small" as const,
          sx: {
            alignSelf: "center",
            cursor: canEditStatus ? "pointer" : "default",
          },
        };

  const openDialog = () => {
    if (!canEditStatus || isPending) {
      return;
    }

    setIsTargetSelected(false);
    setOpen(true);
  };

  const closeDialog = () => {
    if (isPending) {
      return;
    }

    setIsTargetSelected(false);
    setOpen(false);
  };

  const saveStatus = () => {
    if (!isTargetSelected) {
      return;
    }

    updatePatientStatus({
      patientId: patient.patientId,
      status: targetStatus,
    });
  };

  return (
    <>
      <StatusChip
        {...statusChipProps}
        status={currentStatus === ActiveStatus.Active ? "Active" : "Inactive"}
        onClick={canEditStatus ? openDialog : undefined}
      />

      {canEditStatus && (
        <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
          <DialogTitle component="div">
            <Typography variant="h5">Update Patient Status</Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.25}>
              <Stack spacing={0.8} alignItems="center">
                <PatientChip label={patient.displayId} size="large" />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{ fontWeight: 600 }}
              >
                is currently
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                }}
              >
                <Stack spacing={0.8} alignItems="center">
                  <StatusChip
                    status={
                      currentStatus === ActiveStatus.Active
                        ? "Active"
                        : "Inactive"
                    }
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {currentStatus === ActiveStatus.Active
                      ? "Patient is available for normal scheduling."
                      : "Meetings cannot be created while inactive."}
                  </Typography>
                </Stack>
              </Paper>

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{ fontWeight: 600 }}
              >
                but can be made
              </Typography>

              <Paper
                variant="outlined"
                onClick={() => {
                  if (isPending) {
                    return;
                  }

                  setIsTargetSelected(true);
                }}
                sx={(theme) => ({
                  p: 1.25,
                  borderRadius: 1.5,
                  cursor: isPending ? "default" : "pointer",
                  borderColor: isTargetSelected
                    ? theme.palette.primary.main
                    : undefined,
                  backgroundColor: isTargetSelected
                    ? theme.palette.action.selected
                    : theme.palette.background.paper,
                  transition:
                    "background-color 120ms ease, border-color 120ms ease",
                  "&:hover": {
                    backgroundColor: isPending
                      ? isTargetSelected
                        ? theme.palette.action.selected
                        : theme.palette.background.paper
                      : theme.palette.action.hover,
                  },
                })}
              >
                <Stack spacing={0.8} alignItems="center">
                  <StatusChip
                    status={
                      targetStatus === ActiveStatus.Active
                        ? "Active"
                        : "Inactive"
                    }
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {targetStatus === ActiveStatus.Active
                      ? "Re-enables scheduling for this patient."
                      : "Keeps history but prevents scheduling new meetings."}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              fullWidth
              onClick={closeDialog}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={saveStatus}
              disabled={isPending || !isTargetSelected}
              startIcon={<CheckIcon />}
              sx={{
                borderRadius: "10rem",
              }}
            >
              {isPending ? "Saving..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default EditablePatientStatusChip;
